import * as React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { formatTime } from '@/components/ui/time';

interface DeleteSiteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** True when the site has an `active` or `trialing` Stripe sub. */
  hasActiveSub: boolean;
  /** True when the active sub has already been flagged to cancel at period end. */
  cancelAtPeriodEnd: boolean;
  /** Period end ISO string — used in the active-sub messaging. */
  currentPeriodEnd: string | null;
  /**
   * Confirmed action. The dialog itself IS the confirmation, so this should
   * call the API directly without surfacing additional confirm prompts. When
   * the site has an active not-yet-cancelling sub, the implementation should
   * cancel the sub at period end AND destroy the site.
   */
  onConfirm: () => Promise<void> | void;
}

export const DeleteSiteDialog: React.FC<DeleteSiteDialogProps> = ({
  open,
  onOpenChange,
  hasActiveSub,
  cancelAtPeriodEnd,
  currentPeriodEnd,
  onConfirm,
}) => {
  const [busy, setBusy] = React.useState(false);

  const periodLabel = currentPeriodEnd ? formatTime(currentPeriodEnd, 'medium') : null;
  const willCancelSub = hasActiveSub && !cancelAtPeriodEnd;

  const title = willCancelSub ? 'You have an active subscription' : 'Delete this site?';
  const description = willCancelSub
    ? `Deleting this site will cancel its subscription so it doesn’t recur. The subscription stays active${periodLabel ? ` until ${periodLabel}` : ''} — no refund for remaining days — but the site comes offline immediately.`
    : hasActiveSub
      ? `This site comes offline immediately. Your subscription will still end on ${periodLabel ?? 'the cancellation date'} — no refund for remaining days.`
      : 'This site comes offline immediately. The row is preserved for audit but the deploy and DNS are torn down.';
  const confirmLabel = willCancelSub ? 'Delete and cancel' : 'Delete site';

  const handleConfirm = async () => {
    setBusy(true);
    try {
      await onConfirm();
      onOpenChange(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={busy ? undefined : onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="tertiary" disabled={busy} onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="destructive" disabled={busy} onClick={handleConfirm}>
            {busy ? (
              <><Spinner size="sm" className="mr-2" />{confirmLabel}</>
            ) : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DeleteSiteDialog;
